<script lang="ts">
  import { getStorage } from '../../lib/storage';
  import type { ProcessedFile } from '../../lib/types';
  import Button from '../common/Button.svelte';
  import FormInput from '../common/FormInput.svelte';
  import Modal from '../common/Modal.svelte';

  let { onUpdate = () => {} }: { onUpdate?: () => void } = $props();

  let files = $state<ProcessedFile[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let successMessage = $state<string | null>(null);

  // Edit modal state
  let editingFile = $state<ProcessedFile | null>(null);
  let editForm = $state({
    bank: '',
    person: '',
    accountType: '',
    accountName: ''
  });

  // Delete confirmation
  let deletingFile = $state<ProcessedFile | null>(null);

  async function loadFiles() {
    try {
      loading = true;
      error = null;
      const storage = getStorage();
      files = await storage.getProcessedFiles();
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to load files';
      console.error('Error loading processed files:', err);
    } finally {
      loading = false;
    }
  }

  function handleEdit(file: ProcessedFile) {
    editingFile = file;
    editForm = {
      bank: file.bank || '',
      person: file.person || '',
      accountType: file.accountType || '',
      accountName: file.accountName || ''
    };
  }

  async function handleSaveEdit() {
    if (!editingFile) return;

    try {
      const storage = getStorage();
      // Update file metadata
      await storage.markFileAsProcessed(
        editingFile.filename,
        editingFile.transactionCount,
        editingFile.fileHash,
        {
          bank: editForm.bank || undefined,
          person: editForm.person || undefined,
          accountType: editForm.accountType || undefined,
          accountName: editForm.accountName || undefined
        }
      );

      // TODO: Update all transactions from this file with new metadata
      // This would require a new storage operation

      await loadFiles();
      editingFile = null;
      onUpdate();
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to update file';
      console.error('Error updating file:', err);
    }
  }

  function handleCancelEdit() {
    editingFile = null;
  }

  function handleDeleteClick(file: ProcessedFile) {
    deletingFile = file;
  }

  async function handleConfirmDelete() {
    if (!deletingFile) return;

    // Save filename before clearing deletingFile
    const filename = deletingFile.filename;

    try {
      const storage = getStorage();
      const result = await storage.deleteProcessedFile(filename);

      if (result.success) {
        // Close modal first
        deletingFile = null;

        // Show success message briefly
        successMessage = `✅ Successfully deleted ${filename} and ${result.deletedTransactions} transaction(s)`;
        setTimeout(() => {
          successMessage = null;
        }, 5000);

        // Reload the files list
        await loadFiles();

        // Notify parent component to refresh
        onUpdate();
      } else {
        error = result.error || 'Failed to delete file';
        deletingFile = null;
      }
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to delete file';
      console.error('Error deleting file:', err);
      deletingFile = null;
    }
  }

  function handleCancelDelete() {
    deletingFile = null;
  }

  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  function formatDateRange(dateRange: { start: string; end: string } | undefined): string {
    if (!dateRange) return 'N/A';
    const start = new Date(dateRange.start).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
    const end = new Date(dateRange.end).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
    if (start === end) return start;
    return `${start} - ${end}`;
  }

  // Load files on mount
  $effect(() => {
    loadFiles();
  });
</script>

<div class="imported-files-table">
  <div class="header">
    <h3>Imported Files</h3>
    <Button variant="secondary" size="sm" onclick={loadFiles}>
      Refresh
    </Button>
  </div>

  {#if successMessage}
    <div class="success-message">{successMessage}</div>
  {/if}

  {#if loading}
    <div class="empty-state">Loading imported files...</div>
  {:else if error}
    <div class="error-message">{error}</div>
  {:else if files.length === 0}
    <div class="empty-state">No files have been imported yet.</div>
  {:else}
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Filename</th>
            <th>Date Range</th>
            <th>Imported</th>
            <th>Count</th>
            <th>Bank</th>
            <th>Person</th>
            <th>Account</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {#each files as file}
            <tr>
              <td class="filename">{file.filename}</td>
              <td class="date-range">{formatDateRange(file.dateRange)}</td>
              <td>{formatDate(file.processedAt)}</td>
              <td class="count">{file.transactionCount}</td>
              <td>{file.bank || '-'}</td>
              <td>{file.person || '-'}</td>
              <td>{file.accountType || file.accountName || '-'}</td>
              <td class="actions">
                <Button variant="secondary" size="sm" onclick={() => handleEdit(file)}>
                  Edit
                </Button>
                <Button variant="danger" size="sm" onclick={() => handleDeleteClick(file)}>
                  Delete
                </Button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

<!-- Edit Modal -->
{#if editingFile}
  <Modal
    isOpen={true}
    title="Edit File Metadata"
    onClose={handleCancelEdit}
  >
    <div class="edit-form">
      <p class="info-message">
        Editing: <strong>{editingFile.filename}</strong><br>
        <span class="note">Changes will update this file's metadata. Transaction updates coming soon.</span>
      </p>

      <FormInput
        label="Bank"
        bind:value={editForm.bank}
        placeholder="e.g., Amex, Chase, etc."
      />

      <FormInput
        label="Person"
        bind:value={editForm.person}
        placeholder="e.g., Shelly, Reece, etc."
      />

      <FormInput
        label="Account Type"
        bind:value={editForm.accountType}
        placeholder="e.g., Credit Card, Checking, etc."
      />

      <FormInput
        label="Account Name"
        bind:value={editForm.accountName}
        placeholder="e.g., Blue Cash, Premier Checking, etc."
      />

      <div class="modal-actions">
        <Button variant="secondary" onclick={handleCancelEdit}>
          Cancel
        </Button>
        <Button variant="primary" onclick={handleSaveEdit}>
          Save Changes
        </Button>
      </div>
    </div>
  </Modal>
{/if}

<!-- Delete Confirmation Modal -->
{#if deletingFile}
  <Modal
    isOpen={true}
    title="Delete Imported File"
    onClose={handleCancelDelete}
  >
    <div class="delete-confirmation">
      <p class="warning-message">
        Are you sure you want to delete <strong>{deletingFile.filename}</strong>?
      </p>
      <p class="note">
        This will remove <strong>{deletingFile.transactionCount} transactions</strong> from your database.
        This action cannot be undone.
      </p>

      <div class="modal-actions">
        <Button variant="secondary" onclick={handleCancelDelete}>
          Cancel
        </Button>
        <Button variant="danger" onclick={handleConfirmDelete}>
          Delete File & Transactions
        </Button>
      </div>
    </div>
  </Modal>
{/if}

<style>
.imported-files-table {
  margin-top: var(--spacing-2xl);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
}

.header h3 {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}

table {
  width: 100%;
  border-collapse: collapse;
  background: var(--color-background);
}

thead {
  background: var(--color-background-secondary);
  border-bottom: 2px solid var(--color-border);
}

th {
  text-align: left;
  padding: var(--spacing-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
}

td {
  padding: var(--spacing-md);
  font-size: var(--font-size-sm);
  border-top: 1px solid var(--color-border-light);
}

.filename {
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.count {
  text-align: center;
  font-weight: var(--font-weight-medium);
}

.date-range {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.actions {
  display: flex;
  gap: var(--spacing-sm);
}

.empty-state {
  padding: var(--spacing-4xl) var(--spacing-2xl);
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: var(--font-size-sm);
}

.error-message {
  padding: var(--spacing-md);
  background: var(--color-danger-light);
  border: 1px solid var(--color-danger-border);
  border-radius: var(--radius-md);
  color: var(--color-danger-dark);
  font-size: var(--font-size-sm);
}

.success-message {
  padding: var(--spacing-md);
  background: var(--color-success-light);
  border: 1px solid var(--color-success-border);
  border-radius: var(--radius-md);
  color: var(--color-success-dark);
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-md);
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.info-message {
  padding: var(--spacing-md);
  background: var(--color-primary-light);
  border: 1px solid var(--color-primary-border);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0;
}

.warning-message {
  padding: var(--spacing-md);
  background: var(--color-warning-light);
  border: 1px solid var(--color-warning-border);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-md) 0;
}

.note {
  display: block;
  margin-top: var(--spacing-sm);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

.delete-confirmation {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
}

@media (max-width: 768px) {
  .table-wrapper {
    overflow-x: scroll;
  }

  table {
    min-width: 800px;
  }

  .actions {
    flex-direction: column;
  }
}
</style>
